/*
 * @Author: Yun
 * @Date: 2025-09-24 15:40:58
 * @Last Modified by: Yun
 * @Last Modified time: 2025-09-25 11:01:36
 */
import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { useGSAP } from '@gsap/react';
import { content } from '@configs/content';
import styles from './index.module.scss';

gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);

// 定义文章内容类型
export interface ArticleItem {
    id: string;
    type: 'paragraph' | 'quote';
    text: string;
    highlights?: string[];
    author?: string;
}

// 定义新闻内容类型
export interface NewsContent {
    title: string;
    article: string | string[];  // 支持字符串或字符串数组
    highlights?: string[];
}

interface SmartReaderProps {
    isModal?: boolean;
    content?: NewsContent;
}

const SmartReader: React.FC<SmartReaderProps> = ({ isModal = false, content: newsContent }) => {
    // 直接从content获取所有数据
    const { defaultTitle, defaultSubtitle, defaultCategory } = content.smartReader.text;
    const article = content.smartReader.article;
    const decorations = content.smartReader.decorations;

    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const articleRef = useRef<HTMLDivElement>(null);
    const scanlineRef = useRef<HTMLDivElement>(null);


    useGSAP(() => {
        if (!containerRef.current || !headerRef.current || !articleRef.current) return;

        const container = containerRef.current;
        const header = headerRef.current;
        const articleElement = articleRef.current;

        // ========== 入场动画 ==========
        const masterTimeline = gsap.timeline({
            scrollTrigger: {
                trigger: container,
                start: 'top 80%',
                end: 'top 30%',
                toggleActions: 'play none none reverse',
            },
        });

        // 扫描线动画
        if (scanlineRef.current) {
            masterTimeline.fromTo(
                scanlineRef.current,
                {
                    scaleX: 0,
                    transformOrigin: 'left center',
                },
                {
                    scaleX: 1,
                    duration: 1.2,
                    ease: 'power3.inOut',
                },
                '-=0.5'
            );
        }

        // Header文字动画
        const headerSplit = new SplitText(header.querySelectorAll('h2, h3, .category'), {
            type: 'lines',
            linesClass: 'line-mask',
        });

        masterTimeline.fromTo(
            headerSplit.lines,
            {
                y: 100,
                opacity: 0,
            },
            {
                y: 0,
                opacity: 1,
                duration: 1,
                stagger: 0.1,
                ease: 'power3.out',
            },
            '-=0.8'
        );

        // ========== 文章内容渐进显示 ==========
        const paragraphs = articleElement.querySelectorAll(`.${styles.paragraph}, .${styles.quote}`);

        paragraphs.forEach((paragraph, index) => {
            // 为每个段落创建SplitText
            const paragraphSplit = new SplitText(paragraph, {
                type: 'lines',
                linesClass: 'reveal-line',
            });

            // 段落滚动动画
            ScrollTrigger.create({
                trigger: paragraph,
                start: 'top 85%',
                end: 'bottom bottom',
                animation: gsap.fromTo(
                    paragraphSplit.lines,
                    {
                        y: 60,
                        opacity: 0,
                        filter: 'blur(8px)',
                    },
                    {
                        y: 0,
                        opacity: 1,
                        filter: 'blur(0px)',
                        duration: 1.2,
                        stagger: 0.08,
                        ease: 'power3.out',
                    }
                ),
                toggleActions: 'play none none reverse',
            });

            // 高亮词汇动画 - 暂时注释掉
            // const highlights = paragraph.querySelectorAll(`.${styles.highlight}`);
            // if (highlights.length > 0) {
            //     ScrollTrigger.create({
            //         trigger: paragraph,
            //         start: 'top 95%',
            //         end: 'bottom bottom',
            //         animation: gsap.fromTo(
            //             highlights,
            //             {
            //                 opacity: 0,
            //                 scale: 0.8,
            //                 backgroundColor: 'transparent',
            //             },
            //             {
            //                 opacity: 1,
            //                 scale: 1,
            //                 backgroundColor: decorations.highlightColor,
            //                 duration: 0.6,
            //                 stagger: 0.1,
            //                 ease: 'back.out(1.7)',
            //             }
            //         ),
            //         toggleActions: 'play none none reverse',
            //     });
            // }
        });


        // ========== 背景粒子效果 ==========
        createParticleEffect();

        // 清理函数
        return () => {
            if (headerSplit) headerSplit.revert();
        };
    }, [article, decorations]);

    // 创建背景粒子效果
    const createParticleEffect = () => {
        const particleContainer = containerRef.current?.querySelector(`.${styles.particles}`);
        if (!particleContainer) return;

        // 创建随机粒子
        for (let i = 0; i < 20; i++) {
            const particle = document.createElement('div');
            particle.className = styles.particle;

            // 随机位置和大小
            const x = Math.random() * 100;
            const y = Math.random() * 100;
            const size = Math.random() * 3 + 1;

            particle.style.left = `${x}%`;
            particle.style.top = `${y}%`;
            particle.style.width = `${size}px`;
            particle.style.height = `${size}px`;
            particle.style.backgroundColor = decorations.particleColor;

            particleContainer.appendChild(particle);

            // 粒子动画
            gsap.to(particle, {
                y: -50,
                opacity: 0,
                duration: Math.random() * 10 + 5,
                repeat: -1,
                delay: Math.random() * 5,
                ease: 'none',
            });
        }
    };

    // 处理高亮文本 - 暂时注释掉高亮逻辑
    const renderTextWithHighlights = (text: string, highlights: string[] = []) => {
        let processedText = text;

        // 暂时注释掉高亮逻辑，让文本保持普通样式
        // if (highlights && highlights.length > 0) {
        //     highlights.forEach((highlight) => {
        //         const regex = new RegExp(`(${highlight})`, 'gi');
        //         processedText = processedText.replace(regex, `<span class="${styles.highlight}">$1</span>`);
        //     });
        // }

        return { __html: processedText };
    };

    // Modal 模式的 refs
    const modalTitleRef = useRef<HTMLHeadingElement>(null);
    const modalContentRef = useRef<HTMLDivElement>(null);

    // Modal 模式动画
    useGSAP(() => {
        if (!isModal || !newsContent) return;
        if (!modalTitleRef.current || !modalContentRef.current) return;

        const tl = gsap.timeline({ delay: 0.3 }); // 等待弹窗动画完成

        // 标题简单淡入
        tl.fromTo(
            modalTitleRef.current,
            {
                opacity: 0,
                y: 20,
            },
            {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power3.out',
            }
        );

        // 对每个段落单独进行动画，而不是整体分割成行
        const paragraphs = modalContentRef.current.querySelectorAll('p');
        
        paragraphs.forEach((paragraph, index) => {
            // 为每个段落创建 SplitText
            const paragraphSplit = new SplitText(paragraph, {
                type: 'lines',
                linesClass: 'reveal-line',
            });

            tl.fromTo(
                paragraphSplit.lines,
                {
                    y: 60,
                    opacity: 0,
                    filter: 'blur(8px)',
                },
                {
                    y: 0,
                    opacity: 1,
                    filter: 'blur(0px)',
                    duration: 1.2,
                    stagger: 0.08,
                    ease: 'power3.out',
                },
                index === 0 ? '-=0.3' : '-=1.0'  // 第一个段落延迟少一点，后续段落有重叠
            );
        });

        return () => {
            // 清理所有段落的 SplitText
            const paragraphs = modalContentRef.current?.querySelectorAll('p');
            if (paragraphs) {
                paragraphs.forEach(p => {
                    const split = new SplitText(p);
                    split.revert();
                });
            }
        };
    }, [isModal, newsContent]);

    const renderModalContent = () => {
        if (!newsContent) return null;
        
        const article = newsContent.article;
        
        if (Array.isArray(article)) {
            return article.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
            ));
        }
        
        return <p>{article}</p>;
    };
        

    // Modal 模式：简化渲染但保留动画
    if (isModal && newsContent) {
        return (
            <div className={`${styles.smartReader} ${styles.modalMode}`}>
                <article className={styles.modalArticle}>
                    <h1 ref={modalTitleRef} className={styles.modalTitle}>
                        {newsContent.title}
                    </h1>
                    <div ref={modalContentRef} className={styles.modalContent}>
                        {renderModalContent()}
                    </div>
                </article>
            </div>
        );
    }

    // 原有的完整模式
    return (
        <section ref={containerRef} className={styles.smartReader}>
            {/* 背景粒子 */}
            <div className={styles.particles}></div>

            {/* 扫描线 */}
            <div ref={scanlineRef} className={styles.scanline}></div>


            {/* Header区域 */}
            <header ref={headerRef} className={styles.header}>
                <div className={`${styles.category} category`}>{defaultCategory}</div>
                <h2 className={styles.title}>{defaultTitle}</h2>
                <h3 className={styles.subtitle}>{defaultSubtitle}</h3>
            </header>

            {/* 文章内容 */}
            <article ref={articleRef} className={styles.article}>
                <div className={styles.articleHeader}>
                    <h1 className={styles.articleTitle}>{article.title}</h1>
                    <p className={styles.articleSubtitle}>{article.subtitle}</p>
                </div>

                <div className={styles.content}>
                    {article.content.map((item) => (
                        <div key={item.id} className={styles.contentItem}>
                            {item.type === 'paragraph' ? (
                                <p
                                    className={styles.paragraph}
                                    dangerouslySetInnerHTML={renderTextWithHighlights(item.text, item.highlights)}
                                />
                            ) : (
                                <blockquote className={styles.quote}>
                                    <p className={styles.quoteText} dangerouslySetInnerHTML={renderTextWithHighlights(item.text, [])} />
                                    {item.author && <cite className={styles.quoteAuthor}>— {item.author}</cite>}
                                </blockquote>
                            )}
                        </div>
                    ))}
                </div>
            </article>

            {/* 装饰元素 */}
            <div className={styles.decorations}>
                <div className={styles.techGrid}></div>
                <div className={styles.glowEffect}></div>
            </div>
        </section>
    );
};

export default SmartReader;
